import type { ReactNode } from "react";

import { Layout as AntLayout } from "antd";
import { clsx } from "clsx";
import { Helmet } from "react-helmet";

import styles from "./RVAppLayout.module.scss";

/**
 * RVAppLayout
 *
 * Main layout component for Regal apps. Handles sidebar, header, and content area.
 *
 * @param {boolean} showSidebar - Whether to show the sidebar
 * @param {string} [pageTitle] - Page title for the document
 * @param {boolean} [shouldHideSidenavWhenEmbedded] - Hide sidebar when embedded
 * @param {boolean} [isAppEmbedded] - Is the app embedded in another context
 * @param {ReactNode} [sideNav] - Sidebar content
 * @param {boolean} [loading] - Show loading state
 * @param {ReactNode} children - Main content
 * @param {ReactNode} [header] - Header content
 * @param {string} appTitle - App name for document title
 * @returns {JSX.Element}
 */
type RVAppLayoutProps = {
    /** Whether to show the sidebar */
    showSidebar: boolean;
    /** Page title for the document */
    pageTitle?: string;
    /** Hide sidebar when embedded */
    shouldHideSidenavWhenEmbedded?: boolean;
    /** Is the app embedded in another context */
    isAppEmbedded?: boolean;
    /** Sidebar content */
    sideNav?: ReactNode;
    /** Show loading state */
    loading?: boolean;
    /** Main content */
    children: ReactNode;
    /** Header content */
    header?: ReactNode;
    /** App name for document title */
    appTitle: string;
};

/**
 * RVAppLayout
 *
 * Main layout for Regal apps. Handles sidebar, header, and main content area.
 */
export function RVAppLayout({
    showSidebar,
    pageTitle,
    shouldHideSidenavWhenEmbedded,
    isAppEmbedded,
    sideNav,
    loading,
    children,
    header,
    appTitle,
}: RVAppLayoutProps): JSX.Element {
    return (
        <AntLayout className={styles.rootLayout}>
            <Helmet>
                <title>
                    {pageTitle ? `${pageTitle} | ` : ""}
                    {appTitle}
                </title>
            </Helmet>
            {header}
            <AntLayout className={styles.body}>
                {showSidebar && !shouldHideSidenavWhenEmbedded && sideNav}
                <AntLayout.Content
                    className={clsx(styles.content, {
                        [styles.minimal]: !showSidebar,
                        [styles.embedded]: isAppEmbedded,
                    })}
                >
                    {loading ? <div>Loading your content...</div> : children}
                </AntLayout.Content>
            </AntLayout>
        </AntLayout>
    );
}
